import iconsPng from '@/assets/img/png/icons';
import ModalContainer from '@/components/molecules/ModalContainer';
import HeroBackgroundPage from '@/components/templates/HeroBackgroundPage';
import { CREATOR_APPLICATION_BACKGROUND, CREATOR_APPLICATION_BACKGROUND_OPACITY } from '@/constants/creatorApplication';
import { EMAIL_REGEX, RIGHT_ARROW_CHARACTER } from '@/constants/stringVars';
import { useAuthContext } from '@/context/auth/useAuthContext';
import useRecoverContentCreatorApplication from '@/hooks/contentcreator/useRecoverContentCreatorApplication';
import { yupResolver } from '@hookform/resolvers/yup';
import { CheckIcon } from '@chakra-ui/icons';
import { Box, Button, FormControl, HStack, Image, Input, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

type RecoverApplicationFormData = {
  email: string;
};

const recoverApplicationSchema = yup
  .object({
    email: yup.string().required('Email is required.').matches(EMAIL_REGEX, 'Please enter valid email address.'),
  })
  .required();

const creatorOptions = [
  {
    title: 'Create Your First Reel',
    description: 'Share reels and guide positive change',
  },
  {
    title: 'Write Your First Article',
    description: 'Inspire with your story and insights',
  },
  {
    title: 'Share a Brief Post',
    description: 'Thoughts, ideas and comments',
  },
];

const SparkleBadge = () => (
  <Box
    width={{ base: '64px', md: '80px' }}
    height={{ base: '64px', md: '80px' }}
    borderRadius={'12px'}
    backgroundColor={'white'}
    display={'flex'}
    alignItems={'center'}
    justifyContent={'center'}
    boxShadow={'0px 16px 40px 0px #411AAB80'}
  >
    <Image src={iconsPng.creator} alt="" width="44px" height="44px" />
  </Box>
);

const RecoverApplicationModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { mutate: recoverContentCreatorApplication, isPending } = useRecoverContentCreatorApplication();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<RecoverApplicationFormData>({
    resolver: yupResolver(recoverApplicationSchema),
    mode: 'all',
    defaultValues: {
      email: '',
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: RecoverApplicationFormData) => {
    recoverContentCreatorApplication(data, {
      onSuccess: handleClose,
    });
  };

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleClose}
      isBlur={false}
      width={'min(420px, calc(100vw - 32px))'}
      backgroundColor={'background.lightBlue'}
      maxWidth={'420px'}
    >
      <VStack
        as={'form'}
        onSubmit={handleSubmit(onSubmit)}
        paddingTop={'48px'}
        paddingBottom={'34px'}
        paddingX={{ base: '18px', md: '34px' }}
        spacing={'18px'}
      >
        <Text variant={'loraSmallTitle'} color={'black'} textAlign={'center'}>
          Enter Your Email
        </Text>

        <Text variant={'urbanistMediumLarge'} color={'text.mediumGray'} textAlign={'center'}>
          We will resend the application link to the address you input below
        </Text>

        <FormControl marginTop={'10px'}>
          <Input className="custom-input" placeholder={'Email Address'} type={'email'} {...register('email')} />
          <Text variant={'error'} marginTop={'5px'}>
            {errors.email?.message ?? ''}
          </Text>
        </FormControl>

        <Button
          variant={'continue'}
          width={'100%'}
          maxWidth={'318px'}
          type={'submit'}
          isLoading={isPending}
          isDisabled={!isValid || isPending}
          disabled={!isValid || isPending}
        >
          Resend
        </Button>
      </VStack>
    </ModalContainer>
  );
};

const CreatorApplyHero = () => {
  const { isAuthenticated } = useAuthContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const applyFormRoute = isAuthenticated ? '/creator/apply/application' : '/creator/apply/form';

  return (
    <>
      <VStack
        maxHeight={'100vh'}
        width={'100vw'}
        justifyContent={{ base: 'start', md: 'flex-start' }}
        paddingX={'24px'}
        spacing={{ base: '28px', md: '42px' }}
        marginTop={'-120px'}
      >
        <Text variant={'loraTitle'} color={'white'} fontSize={'26px'} lineHeight={'32px'} fontWeight={'400'}>
          Become a Creator and Inspire Change
        </Text>

        <SparkleBadge />

        <Text
          color={'white'}
          fontSize={{ base: '15px', md: '16px' }}
          fontWeight={'600'}
          lineHeight={{ base: '20px', md: '18px' }}
          textAlign={'center'}
          maxWidth={'600px'}
        >
          You’ve faced challenges, embraced discomfort, and emerged stronger. Now, share your journey to help others
          discover their strength and show them that, though change is hard, it’s always possible
        </Text>

        <VStack spacing={{ base: '22px', md: '24px' }} alignItems={'flex-start'} maxWidth={'360px'} width={'100%'}>
          {creatorOptions.map((option) => (
            <HStack key={option.title} spacing={'12px'} alignItems={'flex-start'}>
              <Box
                width={'18px'}
                height={'18px'}
                borderRadius={'50%'}
                backgroundColor={'secondary.500'}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
                flexShrink={0}
                marginTop={'2px'}
              >
                <CheckIcon color={'white'} boxSize={'9px'} />
              </Box>
              <VStack alignItems={'flex-start'} spacing={'8px'}>
                <Text color={'secondary.500'} fontSize={'16px'} fontWeight={'900'} lineHeight={'20px'}>
                  {option.title}
                </Text>
                <Text color={'white'} fontSize={'16px'} fontWeight={'600'} lineHeight={'20px'}>
                  {option.description}
                </Text>
              </VStack>
            </HStack>
          ))}
        </VStack>

        <VStack width={'100%'} maxWidth={'360px'} spacing={'22px'} marginTop={{ base: '6px', md: '14px' }}>
          <Button as={Link} to={applyFormRoute} variant={'continue'} width={'100%'}>
            Apply to Become a Creator
          </Button>

          <HStack justifyContent={'center'} flexWrap={'wrap'}>
            <Text color={'white'} fontSize={'14px'} fontWeight={'600'} lineHeight={'20px'} opacity={0.7}>
              Already started an application?
            </Text>
            <HStack
              as={'span'}
              spacing={'4px'}
              color="white"
              cursor={'pointer'}
              _hover={{ color: 'secondary.400', textDecoration: 'none' }}
              onClick={onOpen}
            >
              <Text as={'span'} fontWeight={'900'} fontSize={'14px'}>
                Check status here
              </Text>
              <Text as={'span'} fontWeight={'900'} fontSize={'12px'} marginTop={'1px'}>
                {RIGHT_ARROW_CHARACTER}
              </Text>
            </HStack>
          </HStack>
        </VStack>
      </VStack>

      <RecoverApplicationModal isOpen={isOpen} onClose={onClose} />
    </>
  );
};

const CreatorApply = () => {
  return (
    <HeroBackgroundPage
      heroChildren={<CreatorApplyHero />}
      backgroundOverlay={CREATOR_APPLICATION_BACKGROUND}
      backgroundOverlayOpacity={CREATOR_APPLICATION_BACKGROUND_OPACITY}
    />
  );
};

export const Route = createFileRoute('/creator/apply/')({
  staticData: { title: 'Become a Creator' },
  component: CreatorApply,
});
